import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../../services/auth.service';
import { Mail, RefreshCw, CheckCircle, AlertCircle, ArrowLeft, Clock, Shield, Sparkles } from 'lucide-react';
import logo from '../../assets/logoblanc.png';

interface LocationState {
  email?: string;
  userType?: 'user' | 'training_center' | 'consultant';
}

const EmailVerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const state = location.state as LocationState;
  
  // Get email from multiple sources: URL params, state, or localStorage
  const getEmail = () => {
    const urlEmail = searchParams.get('email');
    const stateEmail = state?.email;
    const localStorageEmail = localStorage.getItem('pendingVerificationEmail');
    return urlEmail || stateEmail || localStorageEmail || '';
  };
  
  const [email] = useState(getEmail());
  const [code, setCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes in seconds
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    
    if (!email) {
      navigate('/auth');
      return;
    }
    
    // Clear the localStorage after using it
    if (localStorage.getItem('pendingVerificationEmail')) {
      localStorage.removeItem('pendingVerificationEmail');
      localStorage.removeItem('pendingVerificationUserType');
    }

    // Start countdown timer
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [email, navigate, location.state, state]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
    setError('');
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (code.length !== 6) {
      setError(t('auth.emailVerification.errors.invalidCode'));
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const result = await authService.verifyEmail({ email, code });
      
      if (result.success) {
        setSuccess(true);
        // Token is in httpOnly cookie; store user data and redirect
        setTimeout(() => {
          if (result.user) {
            localStorage.setItem('user', JSON.stringify(result.user));

            const finalRoleId = result.user.role_id || result.user.role?.id;
            const roleParam = searchParams.get('role');

            if (finalRoleId === 2) {
              navigate('/auth/training-center/complete-profile');
            } else if (finalRoleId === 3) {
              navigate('/auth/user');
            } else if (roleParam === 'consultant') {
              navigate('/auth/consultant/complete-profile');
            } else {
              navigate('/dashboard');
            }
          }
        }, 2000);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || t('auth.emailVerification.errors.verification', 'Error during verification');
      setError(errorMessage);
      
      // If verification fails due to invalid/expired code, redirect to registration after a delay
      if (errorMessage.includes('invalide') || errorMessage.includes('expiré') || errorMessage.includes('invalid') || errorMessage.includes('expired')) {
        setTimeout(() => {
          // Redirect to appropriate registration page based on user type
          const roleParam = searchParams.get('role');
          if (state?.userType === 'training_center' || roleParam === 'training_center') {
            navigate('/auth/training-center', { replace: true });
          } else if (state?.userType === 'consultant' || roleParam === 'consultant') {
            navigate('/auth/consultant', { replace: true });
          } else {
            navigate('/auth/user', { replace: true });
          }
        }, 3000);
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setError('');

    try {
      await authService.resendVerification({ email });
      setCode('');
      setTimeLeft(600);
      setCanResend(false);
      
      // Restart countdown
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (error: any) {
      setError(error.response?.data?.message || t('auth.emailVerification.errors.resend', 'Error resending code'));
    } finally {
      setIsResending(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-primary/5 via-brand-secondary/5 to-brand-accent/5 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          {/* Success Card */}
          <div className="bg-white rounded-xl shadow-xl overflow-hidden">
            {/* Header with Brand Colors */}
            <div className="bg-gradient-to-r from-brand-primary via-brand-secondary to-brand-accent p-4 text-center">
              {/* Logo */}
              <div className="mb-3">
                <img src={logo} alt="PHINLEX" className="h-10 w-auto mx-auto" />
              </div>
              
              {/* Success Icon */}
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-3 border border-white/30">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              
              <h1 className="text-xl font-bold text-white mb-1">{t('auth.emailVerification.success.title')}</h1>
              <p className="text-white/90 text-sm">
                {t('auth.emailVerification.success.subtitle')}
              </p>
            </div>

            {/* Content */}
            <div className="p-6 text-center">
              <div className="mb-4">
                <div className="inline-flex items-center space-x-2 px-3 py-2 bg-gradient-to-r from-brand-accent/10 to-brand-secondary/10 rounded-lg">
                  <Sparkles className="w-4 h-4 text-brand-primary" />
                  <span className="text-brand-primary font-medium text-sm">{t('auth.emailVerification.success.redirecting')}</span>
                </div>
              </div>
              
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-brand-primary border-t-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-primary/5 via-brand-secondary/5 to-brand-accent/5 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-xl overflow-hidden">
          {/* Header with Brand Colors */}
          <div className="bg-gradient-to-r from-brand-primary via-brand-secondary to-brand-accent p-4 text-center">
            {/* Logo */}
            <div className="mb-3">
              <img src={logo} alt="PHINLEX" className="h-10 w-auto mx-auto" />
            </div>
            
            {/* Verification Icon */}
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-3 border border-white/30">
              <Mail className="w-6 h-6 text-white" />
            </div>
            
            <h1 className="text-xl font-bold text-white mb-1">{t('auth.emailVerification.title')}</h1>
            <p className="text-white/90 text-sm mb-1">
              {t('auth.emailVerification.subtitle')}
            </p>
            <p className="font-semibold text-white text-sm">{email}</p>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Timer */}
            <div className="text-center mb-4">
              <div className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-brand-accent/10 to-brand-secondary/10 rounded-lg border border-brand-accent/20">
                <Clock className="w-4 h-4 text-brand-primary mr-2" />
                <span className="text-xs text-gray-700">
                  {t('auth.emailVerification.expiresIn')} <span className="font-mono font-bold text-brand-primary">{formatTime(timeLeft)}</span>
                </span>
              </div>
            </div>

            {/* Verification Form */}
            <form onSubmit={handleVerify} className="space-y-4">
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth.emailVerification.codeLabel')}
                </label>
                <div className="relative">
                  <input
                    id="code"
                    type="text"
                    value={code}
                    onChange={handleCodeChange}
                    placeholder={t('auth.emailVerification.codePlaceholder')}
                    className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all duration-200"
                    maxLength={6}
                    autoComplete="off"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <Shield className="w-5 h-5 text-brand-primary/50" />
                  </div>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center">
                    <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
                    <span className="text-sm text-red-700 font-medium">{error}</span>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={isVerifying || code.length !== 6}
                className="w-full bg-gradient-to-r from-brand-primary to-brand-secondary text-white py-3 px-4 rounded-lg font-semibold hover:from-brand-primary/90 hover:to-brand-secondary/90 focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                    {isVerifying ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                        {t('auth.emailVerification.verifying')}
                      </div>
                    ) : (
                      t('auth.emailVerification.verifyButton')
                    )}
              </button>
            </form>

            {/* Resend Code */}
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-600 mb-2">
                {t('auth.emailVerification.resendQuestion')}
              </p>
              <button
                onClick={handleResendCode}
                disabled={!canResend || isResending}
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-brand-accent/10 to-brand-secondary/10 text-brand-primary hover:from-brand-accent/20 hover:to-brand-secondary/20 font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 border border-brand-accent/20 text-sm"
              >
                {isResending ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-2 border-brand-primary border-t-transparent mr-2"></div>
                    {t('auth.emailVerification.sending')}
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    {t('auth.emailVerification.resendButton')}
                  </>
                )}
              </button>
            </div>

            {/* Back to Login */}
            <div className="mt-3 text-center">
              <button
                onClick={() => navigate('/auth')}
                className="inline-flex items-center text-gray-600 hover:text-brand-primary transition-colors duration-200 text-sm"
              >
                <ArrowLeft className="w-3 h-3 mr-1" />
                {t('auth.emailVerification.backToLogin')}
              </button>
            </div>

            {/* Help Section */}
            <div className="mt-4 p-3 bg-gradient-to-r from-brand-accent/5 to-brand-secondary/5 rounded-lg border border-brand-accent/10">
              <h3 className="text-xs font-semibold text-brand-primary mb-2 flex items-center">
                <Sparkles className="w-3 h-3 mr-1" />
                {t('auth.emailVerification.tips')}
              </h3>
              <ul className="text-xs text-gray-700 space-y-1">
                <li className="flex items-start">
                  <span className="text-brand-primary mr-1">•</span>
                  {t('auth.emailVerification.checkSpam')}
                </li>
                <li className="flex items-start">
                  <span className="text-brand-primary mr-1">•</span>
                  {t('auth.emailVerification.codeExpires')}
                </li>
                <li className="flex items-start">
                  <span className="text-brand-primary mr-1">•</span>
                  {t('auth.emailVerification.requestNewCode')}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPage;